import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ToastProvider } from './components/Toast/ToastContainer';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(module => ({ default: module.ServicesPage })));
const MediaPage = lazy(() => import('./pages/MediaPage').then(module => ({ default: module.MediaPage })));
const NewslettersPage = lazy(() => import('./pages/NewslettersPage').then(module => ({ default: module.NewslettersPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(module => ({ default: module.LoginPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(module => ({ default: module.AdminPage })));
const PDFManagementPage = lazy(() => import('./pages/PDFManagementPage').then(module => ({ default: module.PDFManagementPage })));
const ClientManagementPage = lazy(() => import('./pages/ClientManagementPage').then(module => ({ default: module.ClientManagementPage })));
const NewsManagementPage = lazy(() => import('./pages/NewsManagementPage').then(module => ({ default: module.NewsManagementPage })));
const NewsPage = lazy(() => import('./pages/NewsPage').then(module => ({ default: module.NewsPage })));
const ComunicadosManagementPage = lazy(() => import('./pages/ComunicadosManagementPage').then(module => ({ default: module.ComunicadosManagementPage })));
const ComunicadosDirectosPage = lazy(() => import('./pages/ComunicadosDirectosPage').then(module => ({ default: module.ComunicadosDirectosPage })));
const SubscriberManagementPage = lazy(() => import('./pages/SubscriberManagementPage').then(module => ({ default: module.SubscriberManagementPage })));
const ComunicadoViewPage = lazy(() => import('./pages/ComunicadoViewPage').then(module => ({ default: module.ComunicadoViewPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="large" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout />}>
          <Route
            index
            element={
              <Suspense fallback={<PageLoader />}>
                <HomePage />
              </Suspense>
            }
          />
          <Route
            path="nosotros"
            element={
              <Suspense fallback={<PageLoader />}>
                <AboutPage />
              </Suspense>
            }
          />
          <Route
            path="servicios"
            element={
              <Suspense fallback={<PageLoader />}>
                <ServicesPage />
              </Suspense>
            }
          />
          <Route
            path="medios"
            element={
              <Suspense fallback={<PageLoader />}>
                <MediaPage />
              </Suspense>
            }
          />
          <Route
            path="boletines"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewslettersPage />
              </Suspense>
            }
          />
          <Route
            path="noticias"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsPage />
              </Suspense>
            }
          />
          <Route
            path="contacto"
            element={
              <Suspense fallback={<PageLoader />}>
                <ContactPage />
              </Suspense>
            }
          />
          <Route
            path="ver-comunicado"
            element={
              <Suspense fallback={<PageLoader />}>
                <ComunicadoViewPage />
              </Suspense>
            }
          />
        </Route>

        {/* Admin Routes */}
        <Route path="/admin">
          <Route
            path="login"
            element={
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            element={
              <ProtectedRoute redirectPath="/admin/login">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminPage />
                </Suspense>
              }
            />
            <Route
              path="pdfs"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PDFManagementPage />
                </Suspense>
              }
            />
            <Route
              path="content"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PDFManagementPage />
                </Suspense>
              }
            />
            <Route
              path="clients"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClientManagementPage />
                </Suspense>
              }
            />
            <Route
              path="news"
              element={
                <Suspense fallback={<PageLoader />}>
                  <NewsManagementPage />
                </Suspense>
              }
            />
            <Route
              path="comunicados-directos"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ComunicadosDirectosPage />
                </Suspense>
              }
            />
            <Route
              path="comunicados"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ComunicadosManagementPage />
                </Suspense>
              }
            />
            <Route
              path="subscribers"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SubscriberManagementPage />
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* 404 Not Found */}
        <Route
          path="*"
          element={
            <Suspense fallback={<PageLoader />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;